import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopNavigation from 'components/ui/TopNavigation';
import Icon from 'components/AppIcon';
import api from 'lib/api';
import { useAuth } from 'contexts/AuthContext';
import AvatarUpload from './components/AvatarUpload';
import ProfileForm from './components/ProfileForm';

function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[300] flex items-center gap-3 px-4 py-3 rounded-[var(--radius-md)] shadow-[var(--shadow-xl)] text-white text-sm font-[var(--font-caption)] ${
      type === 'success' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-error)]'
    }`}>
      <Icon name={type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} color="#fff" />
      {message}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">
        <Icon name="X" size={14} color="#fff" />
      </button>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="flex flex-col items-center gap-4">
        <div className="w-24 h-24 rounded-full bg-[var(--color-muted)]" />
        <div className="w-48 h-20 rounded-[var(--radius-md)] bg-[var(--color-muted)]" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[...Array(4)]?.map((_, i) => (
          <div key={i} className="h-12 rounded-[var(--radius-sm)] bg-[var(--color-muted)]" />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {[...Array(2)]?.map((_, i) => (
          <div key={i} className="h-12 rounded-[var(--radius-sm)] bg-[var(--color-muted)]" />
        ))}
      </div>
    </div>
  );
}

export default function ProfileUpdatePage() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    avatarUrl: '',
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchProfile = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/profiles/me');
      setFormData({
        fullName: data?.full_name ?? '',
        phone: data?.phone ?? '',
        dateOfBirth: data?.date_of_birth ?? '',
        gender: data?.gender ?? '',
        emergencyContactName: data?.emergency_contact_name ?? '',
        emergencyContactPhone: data?.emergency_contact_phone ?? '',
        avatarUrl: data?.avatar_url ?? '',
      });
    } catch (err) {
      setError(err?.message || 'Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const validate = () => {
    const errs = {};
    if (!formData?.fullName?.trim()) errs.fullName = 'Full name is required.';
    setFormErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const { error: saveErr } = await updateProfile({
        full_name: formData?.fullName?.trim(),
        phone: formData?.phone?.trim() || null,
        date_of_birth: formData?.dateOfBirth || null,
        gender: formData?.gender || null,
        emergency_contact_name: formData?.emergencyContactName?.trim() || null,
        emergency_contact_phone: formData?.emergencyContactPhone?.trim() || null,
        avatar_url: formData?.avatarUrl || null,
      });
      if (saveErr) throw saveErr;
      setToast({ message: 'Profile saved successfully!', type: 'success' });
    } catch (err) {
      setToast({ message: err?.message || 'Failed to save profile.', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (url) => {
    setFormData(prev => ({ ...prev, avatarUrl: url }));
    try {
      const { error: saveErr } = await updateProfile({ avatar_url: url });
      if (saveErr) throw saveErr;
      setToast({ message: 'Profile photo updated!', type: 'success' });
    } catch (err) {
      setToast({ message: err?.message || 'Failed to save photo.', type: 'error' });
    }
  };

  const initials = formData?.fullName
    ?.split(' ')
    ?.slice(0, 2)
    ?.map(n => n?.[0]?.toUpperCase())
    ?.join('') || user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Breadcrumb */}
        {/* <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-5">
          <Link to="/member-portal" className="hover:text-[var(--color-primary)] transition-colors">Member Portal</Link>
          <Icon name="ChevronRight" size={14} color="currentColor" />
          <span className="text-[var(--color-foreground)]">Profile Settings</span>
        </div> */}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Profile Settings</h1>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">Manage your personal information</p>
          </div>
          <button
            onClick={() => navigate('/member-portal')}
            className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40 transition-all font-[var(--font-caption)]"
          >
            <Icon name="ArrowLeft" size={14} color="currentColor" />
            Back
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] p-6 md:p-8">
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <div className="flex flex-col items-center gap-4 py-10">
              <div className="w-12 h-12 rounded-full bg-[var(--color-error)]/10 flex items-center justify-center">
                <Icon name="AlertCircle" size={22} color="var(--color-error)" />
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] text-center">{error}</p>
              <button
                onClick={fetchProfile}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
              >
                <Icon name="RefreshCw" size={14} color="#fff" />
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Avatar Section */}
              <div className="flex justify-center pb-6 border-b border-[var(--color-border)]">
                <AvatarUpload
                  userId={user?.id}
                  avatarUrl={formData?.avatarUrl}
                  initials={initials}
                  onUploadSuccess={handleAvatarUpload}
                />
              </div>

              {/* Form */}
              <ProfileForm
                formData={formData}
                onChange={setFormData}
                errors={formErrors}
                email={user?.email}
              />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-[var(--color-border)]">
                <Link
                  to="/change-password"
                  className="flex items-center gap-2 text-sm text-[var(--color-primary)] hover:underline font-[var(--font-caption)] transition-colors"
                >
                  <Icon name="KeyRound" size={14} color="var(--color-primary)" />
                  Change Password
                </Link>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/member-portal')}
                    className="px-4 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-all font-[var(--font-caption)]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</>
                    ) : (
                      <><Icon name="Save" size={14} color="#fff" />Save Changes</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
